/**
 * The agent's command timeout — the old web's `AgentTimeoutCard`, on the
 * agent's `/settings/timeout` (item BH5). A select over the agent's own
 * options; the default is marked; saving is immediate.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { formatTimeoutOption, getAgentTimeout, setAgentTimeout, type AgentTimeoutSettings } from "../../lib/agentSettings";
import { Spinner } from "./Spinner";
import { SettingsIcon } from "./SettingsIcon";

export function AgentTimeoutCard({ token }: { token: string | null }) {
  const { t } = useLocale();
  const [settings, setSettings] = useState<AgentTimeoutSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAgentTimeout(token)
      .then((data) => {
        if (cancelled) return;
        setSettings(data);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setSettings(null);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2500);
    return () => window.clearTimeout(id);
  }, [notice]);

  const change = async (value: string) => {
    if (!token) return;
    setSaving(true);
    try {
      setSettings(await setAgentTimeout(token, Number(value)));
      setNotice({ tone: "ok", text: t("settings.user.timeout.savedDesc") });
    } catch (e) {
      setNotice({ tone: "err", text: `${t("settings.user.timeout.saveErrorTitle")}: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card" data-card="agent-timeout">
      <h3 className="settings-card-title">
        <SettingsIcon name="timer" />
        {t("settings.user.timeout.title")}
      </h3>
      {/* The body already says the agent is unreachable. */}
      {error ? null : <p className="settings-note settings-description">{t("settings.user.timeout.description")}</p>}
      <div className="settings-row">
        {loading ? (
          <span className="settings-note"><Spinner size={14} /> {t("settings.user.timeout.loading")}</span>
        ) : settings ? (
          <select
            className="settings-input timeout-select"
            value={String(settings.baseTimeoutMs)}
            disabled={saving}
            onChange={(e) => void change(e.target.value)}
            aria-label={t("settings.user.timeout.title")}
          >
            {settings.options.map((ms) => (
              <option key={ms} value={String(ms)}>
                {formatTimeoutOption(ms, settings.defaultBaseTimeoutMs, t)}
              </option>
            ))}
          </select>
        ) : (
          <span className="settings-note">{t("settings.user.timeout.unavailable")}</span>
        )}
      </div>
      {notice ? <div className={`settings-msg ${notice.tone}`}>{notice.text}</div> : null}
    </div>
  );
}
